import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, Download, FileText, Loader2, Mail } from 'lucide-react';
import { getCurrencySymbol } from '@shared/utils/currencyOptions';
import { getApiBaseUrl } from '../api/config';
import { resolveShopTheme, themeCssVars, type ShopBranding } from '../theme';
import { useI18n } from '../i18n';
import { formatLongDate } from '../i18n/format';
import type { ShopLegal } from '../legal';
import VoucherCard from '../components/VoucherCard';
import WalletButtons from '../components/WalletButtons';
import { AccentButton, QuietButton, ShopFooter, SmallCaps } from '../components/ShopChrome';
import { downloadVoucherPdf, voucherPdfStrings } from '../utils/voucherPdf';
import { NoVatNote } from '../components/BuyerTypeSection';
import { explainerPath } from '../components/companyDetailsForm';

interface VoucherData {
  id: string;
  code: string;
  amount: number;
  currency: string;
  recipient_name: string;
  recipient_email: string;
  message?: string;
  expiry_date: string;
  purchase_id?: string;
  location_id?: string;
}

const MAX_ATTEMPTS = 20;
const POLL_INTERVAL_MS = 5000;

/** The purchase confirmation is issued a few seconds after the voucher; wait up to ~30 s. */
const DOCUMENT_ATTEMPTS = 10;
const DOCUMENT_RETRY_MS = 3000;

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

function fileNameFrom(disposition: string | null, fallback: string): string {
  const match = disposition && /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

const SuccessPage: React.FC = () => {
  const { purchaseId } = useParams<{ purchaseId: string }>();
  const navigate = useNavigate();
  const { t, locale, applyVenueLocale } = useI18n();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [voucherData, setVoucherData] = useState<VoucherData | null>(null);
  const [branding, setBranding] = useState<ShopBranding | null>(null);
  const [venue, setVenue] = useState<{
    name?: string;
    address?: string;
    country?: string;
    legal?: ShopLegal;
    locationId?: string;
  }>({});
  const [documentState, setDocumentState] = useState<'idle' | 'loading' | 'failed'>('idle');
  const [buyerEmail, setBuyerEmail] = useState<string | null>(null);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  const theme = useMemo(() => resolveShopTheme(branding), [branding]);

  useEffect(() => {
    if (!purchaseId) {
      setError(t('success.error_body'));
      setLoading(false);
      return;
    }

    const apiBaseUrl = getApiBaseUrl();
    let attempts = 0;
    let timer: number | undefined;
    let active = true;

    const loadVenue = async (locationId: string) => {
      try {
        const shopResponse = await fetch(`${apiBaseUrl}/gift-vouchers/shop/${locationId}`);
        if (!shopResponse.ok || !active) return;
        const shopData = await shopResponse.json();
        setBranding(shopData.branding || null);
        setVenue({
          name: shopData.location_name,
          address: shopData.location_address,
          country: typeof shopData.location_country === 'string' ? shopData.location_country : undefined,
          legal: shopData.legal,
          locationId,
        });
        applyVenueLocale(shopData.location_language);
      } catch {
        /* the voucher reads fine on the design defaults */
      }
    };

    /** Resolves true once there is nothing left to wait for. */
    const checkStatus = async (): Promise<boolean> => {
      try {
        const response = await fetch(`${apiBaseUrl}/gift-vouchers/purchase/${purchaseId}`);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.error?.message || t('success.error_load'));
          setLoading(false);
          return true;
        }

        const data = await response.json();

        if (data.voucher) {
          const locationId = data.voucher.location_id || data.purchase.location_id;
          setVoucherData({
            id: data.voucher.id,
            code: data.voucher.code,
            amount: data.voucher.amount,
            currency: data.voucher.currency,
            recipient_name: data.voucher.recipient_name || '',
            recipient_email: data.voucher.recipient_email,
            message: data.voucher.message,
            expiry_date: data.voucher.expiry_date,
            purchase_id: data.purchase.id,
            location_id: locationId,
          });
          setBuyerEmail(data.purchase.buyer_email || null);
          if (locationId) await loadVenue(locationId);
          setLoading(false);
          return true;
        }

        // Paid, but the amount did not match: staff were notified, nothing more will happen here.
        if (data.purchase.status === 'needs_review') {
          setError(t('success.error_review'));
          setLoading(false);
          return true;
        }

        if (['failed', 'cancelled', 'expired'].includes(data.purchase.status)) {
          setError(t('success.error_status', { status: data.purchase.status }));
          setLoading(false);
          return true;
        }

        // Nothing to show yet. /check asks the provider what actually happened and issues the
        // voucher itself, so it is the way out of a webhook that is late, lost, or never sent —
        // which is the normal case for methods that settle through a bank (Wero, iDEAL) where
        // the guest is back on this page before MultiSafepay has notified us. Gating this on
        // `status === 'paid'` meant it only ran once the webhook had already done the work, and
        // a purchase still `pending` sat here spinning until the attempt limit.
        //
        // Skipped on the first pass: the webhook usually wins within a second, and this costs a
        // provider API call each time.
        if (attempts > 0) {
          try {
            await fetch(`${apiBaseUrl}/gift-vouchers/purchase/${purchaseId}/check`, { method: 'POST' });
          } catch {
            /* the webhook may still get there on its own */
          }
        }

        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setError(t('success.error_pending'));
          setLoading(false);
          return true;
        }
        return false;
      } catch (err) {
        console.error('Error fetching voucher data:', err);
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setError(t('success.error_load'));
          setLoading(false);
          return true;
        }
        return false;
      }
    };

    void (async () => {
      const done = await checkStatus();
      if (!done && active) {
        timer = window.setInterval(async () => {
          const finished = await checkStatus();
          if (finished && timer) window.clearInterval(timer);
        }, POLL_INTERVAL_MS);
      }
    })();

    return () => {
      active = false;
      if (timer) window.clearInterval(timer);
    };
  }, [purchaseId, t, applyVenueLocale]);

  const handleDownloadPdf = async () => {
    if (!voucherData) return;
    await downloadVoucherPdf(
      {
        code: voucherData.code,
        amount: voucherData.amount,
        currency: voucherData.currency,
        currencySymbol: getCurrencySymbol(voucherData.currency),
        venueName: venue.name || t('card.gift_voucher'),
        venueAddress: venue.address,
        expiryDate: voucherData.expiry_date,
        message: voucherData.message,
        recipientName: voucherData.recipient_name,
      },
      theme,
      locale,
      voucherPdfStrings(t)
    );
  };

  /**
   * Fetches the purchase confirmation, waiting while the API still answers `not_ready`
   * (it is issued a few seconds after payment), then hands the file to the browser.
   */
  const handleDownloadConfirmation = async () => {
    if (!purchaseId || documentState === 'loading') return;
    const url = `${getApiBaseUrl()}/gift-vouchers/purchase/${encodeURIComponent(purchaseId)}/document`;
    setDocumentState('loading');
    try {
      for (let attempt = 0; attempt < DOCUMENT_ATTEMPTS; attempt += 1) {
        const response = await fetch(url, { cache: 'no-store' });
        if (response.ok) {
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = fileNameFrom(response.headers.get('Content-Disposition'), 'purchase-confirmation.pdf');
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
          setDocumentState('idle');
          return;
        }
        const body = await response.json().catch(() => ({}));
        if (response.status !== 404 || body?.error?.code !== 'not_ready') break;
        await wait(DOCUMENT_RETRY_MS);
      }
      setDocumentState('failed');
    } catch {
      // Network or CORS trouble: let the browser fetch the file itself.
      window.open(url, '_blank', 'noopener');
      setDocumentState('idle');
    }
  };

  const handleResend = async () => {
    if (!purchaseId || resendState === 'sending') return;
    setResendState('sending');
    try {
      const r = await fetch(`${getApiBaseUrl()}/gift-vouchers/purchase/${purchaseId}/resend`, { method: 'POST' });
      setResendState(r.ok ? 'sent' : 'failed');
    } catch {
      setResendState('failed');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-5" style={themeCssVars(theme)}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: theme.accent }} />
        <p style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted, margin: 0 }}>
          {t('success.confirming')}
        </p>
      </div>
    );
  }

  if (error || !voucherData) {
    return (
      <div className="flex min-h-screen items-center justify-center px-5" style={themeCssVars(theme)}>
        <div className="max-w-md text-center">
          <h1 style={{ font: `500 26px/1.2 ${theme.fontFamily}`, color: theme.ink, margin: '0 0 8px' }}>
            {t('success.error_title')}
          </h1>
          <p style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted, margin: '0 0 24px' }}>
            {error || t('success.error_body')}
          </p>
          <AccentButton theme={theme} onClick={() => navigate('/voucher')}>
            {t('voucher.check_label')}
          </AccentButton>
        </div>
      </div>
    );
  }

  const currencySymbol = getCurrencySymbol(voucherData.currency);
  const recipientFirstName = voucherData.recipient_name?.split(' ')[0];

  return (
    <div className="flex min-h-screen flex-col" style={themeCssVars(theme)}>
      <main className="mx-auto w-full max-w-[720px] flex-1 px-5 py-12 sm:px-10 sm:py-16">
        <div
          className="inline-flex items-center gap-2.5 px-3 py-1.5"
          style={{
            background: theme.positiveSurface,
            border: `1px solid ${theme.positiveBorder}`,
            borderRadius: Math.min(theme.radius, 2),
            font: `600 11px/1 ${theme.fontFamily}`,
            letterSpacing: '.16em',
            textTransform: 'uppercase',
            color: theme.positive,
          }}
        >
          <Check className="h-3.5 w-3.5" />
          {t('success.paid')}
        </div>

        <h1
          style={{
            margin: '18px 0 8px',
            font: `500 clamp(26px, 5vw, 34px)/1.15 ${theme.fontFamily}`,
            letterSpacing: '-.02em',
            color: theme.ink,
          }}
        >
          {recipientFirstName ? t('success.title', { name: recipientFirstName }) : t('success.title_generic')}
        </h1>
        <p style={{ margin: '0 0 28px', font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted }}>
          {buyerEmail ? t('success.copy_to_buyer', { email: buyerEmail }) : ''}
          {t('success.voucher_line', {
            code: voucherData.code,
            date: formatLongDate(voucherData.expiry_date, locale),
          })}
        </p>

        <VoucherCard
          theme={theme}
          amount={voucherData.amount}
          currencySymbol={currencySymbol}
          currency={voucherData.currency}
          venueName={venue.name || t('card.gift_voucher')}
          venueAddress={venue.address}
          code={voucherData.code}
          expiryDate={voucherData.expiry_date}
          message={voucherData.message}
        />

        <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
          <QuietButton theme={theme} onClick={handleDownloadPdf} className="w-full" style={{ padding: '14px' }}>
            <Download className="h-4 w-4" />
            {t('common.download_pdf')}
          </QuietButton>
          <QuietButton
            theme={theme}
            onClick={handleResend}
            disabled={resendState === 'sending' || resendState === 'sent'}
            className="w-full"
            style={{ padding: '14px' }}
          >
            {resendState === 'sending' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            {resendState === 'sent' ? t('success.sent_again') : t('success.send_again')}
          </QuietButton>
        </div>

        {resendState === 'failed' && (
          <p className="mt-3" style={{ font: `400 13px/1.5 ${theme.fontFamily}`, color: theme.accent }}>
            {t('success.resend_failed')}
          </p>
        )}

        <div className="mt-4">
          <WalletButtons voucherCode={voucherData.code} theme={theme} />
        </div>

        <div className="mt-6 pt-5" style={{ borderTop: `1px solid ${theme.hairlineSoft}` }}>
          <button
            type="button"
            onClick={handleDownloadConfirmation}
            disabled={documentState === 'loading'}
            className="inline-flex items-center gap-2 underline underline-offset-2 disabled:cursor-wait"
            style={{ font: `500 15px/1.4 ${theme.fontFamily}`, color: theme.ink }}
            data-testid="download-purchase-confirmation"
          >
            {documentState === 'loading' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            {documentState === 'loading'
              ? t('success.purchase_confirmation_preparing')
              : t('success.purchase_confirmation_download')}
          </button>
          {documentState === 'failed' && (
            <p className="mt-2" style={{ font: `400 13px/1.5 ${theme.fontFamily}`, color: theme.accent, margin: '8px 0 0' }}>
              {t('success.purchase_confirmation_failed')}
            </p>
          )}
          <NoVatNote
            theme={theme}
            text={t('success.purchase_confirmation_no_vat')}
            explainerHref={explainerPath(locale, venue.country)}
            className="mt-2"
          />
        </div>

        <div className="mt-10 pt-6" style={{ borderTop: `1px solid ${theme.hairline}` }}>
          <SmallCaps theme={theme}>{t('success.what_happens')}</SmallCaps>
          <p className="mt-2.5" style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.body, margin: 0 }}>
            {voucherData.recipient_email
              ? t('success.emailed_to', { email: voucherData.recipient_email })
              : t('success.ready_to_hand_over')}
            {t('success.spend_note')}
          </p>
        </div>
      </main>

      <ShopFooter
        theme={theme}
        venueName={venue.name}
        venueAddress={venue.address}
        legal={venue.legal}
        locationId={venue.locationId}
      />
    </div>
  );
};

export default SuccessPage;
